import {
	Box,
	CircularProgress,
	Grid,
	IconButton,
	Stack,
	Typography,
} from '@mui/material';

import { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { deleteRelatedEntity } from 'common/api';
import { infoIcon } from 'common/assets/images';
import ButtonComp from 'common/components/Button/ButtonComp';
import InfoTooltip from 'common/components/Tooltip/Tooltip';
import { TABLE_VIEW_PAGE_SIZE, MODULES } from 'common/constants';
import { HTTP_STATUS_CODES } from 'common/constants/HttpStatusConstants';
import {
	DETAILS_LABEL,
	ENTITY_STATUS,
	ENTITY_TYPE,
	GENERATE_MODE,
	getDescName,
	PRC_BUTTON_TEXT,
	RELATIONSHIP_MAPPING_LABEL,
	SUBMIT_CHANGES_MODAL_TYPE,
} from 'common/constants/TaxonomyConstants';
import { TOOLTIP_TEXT } from 'common/constants/Tooltip';
import { RootState } from 'common/store/AppStore';
import {
	setIsRelationshipMappingDeleted,
	setMappingDrawer,
} from 'common/store/reducer/commonReducer';
import {
	setAddedRelationshipMappingIds,
	setIsSubmitChangesButtonDisabled,
} from 'common/store/reducer/taxonomyReducer';
import { hasInventoryManagerRole } from 'common/utils/utilFunctions';

import useTableQuery from 'hooks/useTableQuery';

import { getObligationDetails } from 'features/HorizonScanning/api';

import { getEntityData } from '../../api';
import {
	IEntityData,
	IEntityDetailResponse,
	ITemplateType,
} from '../../api/type-def';
import SubmitChangesModal from '../../EntityLandingPage/SubmitChangesModal';

import { getFormattedEntityValue } from '../mappingUtils';

import { ReactComponent as DrawerClose } from '../../../../../common/assets/images/drawer-close.svg';

import Description from './Overview/Description';
import Details from './Overview/Details';
import RelationMappingTable from './Overview/RelationshipMappingTable/RelationshipMappingTable';

import styles from './PreviewMappingLayout.module.scss';

const { drawerCloseIconColor } = styles;
const { OK, CREATED } = HTTP_STATUS_CODES;
const { GROUP } = ENTITY_TYPE;
const { REMOVE: REMOVE_LABEL, CANCEL: CANCEL_LABEL } = PRC_BUTTON_TEXT;
const { RELATIONSHIP_MAPPING } = TOOLTIP_TEXT;
const { REMOVE } = SUBMIT_CHANGES_MODAL_TYPE;
const { TABLE_SIZE_5 } = TABLE_VIEW_PAGE_SIZE;
const { AI, MANUAL } = GENERATE_MODE;
const { REVIEWED } = ENTITY_STATUS;
const { RISK_REFERENCE_TAXONOMY } = MODULES;

interface IPreviewMappingProps {
	drawerCloseHandler: (isDeleted: boolean) => void;
}

const PreviewMappingLayout = (props: IPreviewMappingProps) => {
	const { drawerCloseHandler } = props;
	const dispatch = useDispatch();

	/* Hooks */
	const { updateTableQuery } = useTableQuery({
		id: 'relationMappingTable',
	});

	/* Redux store */
	const sideDrawerState = useSelector(
		(state: RootState) => state.common.mappingDrawer,
	);
	const selectedEntity = useSelector(
		(state: RootState) => state.common.selectedEntityTypeData,
	);

	const loggedInUser = useSelector(
		(state: RootState) => state.common.loggedInUser,
	);

	const addedRelationshipMappingIds = useSelector(
		(state: RootState) => state.taxonomy.addedRelationshipMappingIds,
	);

	const { id, type, citationGroupId, parentId } = selectedEntity;

	const hasInvManagerAccess = hasInventoryManagerRole(loggedInUser);

	/* Local states */
	const [isloading, setIsLoading] = useState<boolean>(true);
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [isModalLoading, setisModalLoading] = useState<boolean>(false);
	const [entityData, setEntityData] = useState<ITemplateType[]>([]);

	const handleCloseDrawer = useCallback(() => {
		dispatch(setMappingDrawer(!sideDrawerState));
		updateTableQuery({ pageNumber: 0, pageSize: TABLE_SIZE_5 });
		drawerCloseHandler(false);
	}, [sideDrawerState]);

	const setEntityDataBasedOnType = (responseBody: IEntityDetailResponse) => {
		const entityVal = getFormattedEntityValue(responseBody, type);
		setEntityData(entityVal);
	};

	const getDeleteBtnStatus = () => {
		// for AI generated card can only delete pending one
		if (!hasInvManagerAccess) {
			return true;
		} else if (selectedEntity.generateMode === AI) {
			return selectedEntity.reviewStatus === REVIEWED;
			// for existing card can only delete which user add
		} else if (selectedEntity.generateMode === MANUAL) {
			return selectedEntity.createdBy !== loggedInUser?.email;
		}
		return true;
	};

	const fetchEntityData = async () => {
		setIsLoading(true);
		console.log(type, id);
		if (type?.toLowerCase() !== GROUP.toLowerCase()) {
			const response: IEntityDetailResponse = await getEntityData(id, type);
			if (response?.statusCode === OK) {
				const { responseBody } = response;
				setEntityDataBasedOnType(responseBody);
			} else {
				setEntityData([]);
			}
		} else {
			const response: IEntityDetailResponse = await getObligationDetails(
				citationGroupId || id,
			);
			const { responseBody } = response;
			setEntityDataBasedOnType(responseBody);
		}
		setIsLoading(false);
	};

	const handleDeleteEnity = async () => {
		let newParentId = parentId || '';
		let tempId = id;
		if (selectedEntity.swap) {
			tempId = newParentId;
			newParentId = id;
		}
		setisModalLoading(true);
		const response = await deleteRelatedEntity(
			tempId,
			newParentId || '',
			RISK_REFERENCE_TAXONOMY,
			loggedInUser?.email || '',
		);
		if (response.statusCode === OK || response?.statusCode === CREATED) {
			dispatch(setMappingDrawer(!sideDrawerState));
			dispatch(setIsRelationshipMappingDeleted(true));
			dispatch(
				setIsSubmitChangesButtonDisabled(
					addedRelationshipMappingIds.length === 1 &&
						addedRelationshipMappingIds.includes(id),
				),
			);
			dispatch(
				setAddedRelationshipMappingIds(
					addedRelationshipMappingIds.filter((id: string) => id !== tempId),
				),
			);
			drawerCloseHandler(true);
		}
		setisModalLoading(false);
	};

	/* Local effects */
	useEffect(() => {
		fetchEntityData();
	}, [type, selectedEntity]);

	return (
		<>
			<Grid container direction="column" className={styles['entity-container']}>
				{isloading ? (
					<CircularProgress size={90} data-testid="loader" />
				) : (
					<>
						<Stack direction="row" className={styles['entity-header']}>
							<Typography className={styles['entity-title']}>
								{selectedEntity.title ||
									entityData?.filter(
										(obj: IEntityData) => obj.label === 'name',
									)[0].value ||
									''}
							</Typography>
							<IconButton
								size="large"
								onClick={handleCloseDrawer}
								data-testid="close-button-icon"
							>
								<DrawerClose fill={drawerCloseIconColor} />
							</IconButton>
						</Stack>
						<Stack className={styles['entity-info-wrapper']}>
							<Stack className={styles['entity-info']}>
								<Typography className={styles['entity-info-title']}>
									{DETAILS_LABEL}
								</Typography>
								<Stack direction="column">
									<Details entityData={entityData} isPreviewPage />
								</Stack>
							</Stack>
							<Stack className={styles['entity-info']}>
								<Box className={styles['entity-mapping-table-header']}>
									<Typography className={styles['entity-info-title']}>
										{RELATIONSHIP_MAPPING_LABEL}
									</Typography>
									<InfoTooltip
										className={styles['relationship-mapping-info-tooltip']}
										title={RELATIONSHIP_MAPPING}
									>
										<img src={infoIcon} alt="i" />
									</InfoTooltip>
								</Box>
								<RelationMappingTable
									relationId={citationGroupId || id}
									relationType={type}
									isPreviewDrawer
								/>
							</Stack>
							<Stack className={styles['entity-info']}>
								<Typography className={styles['entity-info-title']}>
									{getDescName(type)}
								</Typography>
								<Box className={styles['entity-description-container']}>
									<Description entityData={entityData} />
								</Box>
							</Stack>
						</Stack>
						<Grid item className={styles['entity-footer']}>
							<Stack direction="row" className={styles['entity-btn-group']}>
								<ButtonComp
									btnType="tertiary"
									disabled={getDeleteBtnStatus()}
									clickHandler={() => setIsModalOpen(true)}
									data-testid="preview-remove-btn"
								>
									{REMOVE_LABEL}
								</ButtonComp>
								<Stack
									direction="row"
									className={styles['entity-btn-group-right']}
								>
									<ButtonComp btnType="secondary" onClick={handleCloseDrawer}>
										{CANCEL_LABEL}
									</ButtonComp>
								</Stack>
							</Stack>
						</Grid>
					</>
				)}
			</Grid>
			{isModalOpen && (
				<SubmitChangesModal
					modalType={REMOVE}
					isModalOpen={isModalOpen}
					isSubmitting={isModalLoading}
					modalCloseHandler={() => setIsModalOpen(false)}
					submitDataHandler={handleDeleteEnity}
				/>
			)}
		</>
	);
};

export default PreviewMappingLayout;
